import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useCurrentProfile } from '@/hooks/useCurrentProfile';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { scrollToBottom } from '@/hooks/useChatUtils';

export interface ChatMessage {
  id: string;
  user_id: string;
  content: string;
  created_at: string;
  edited_at?: string | null;
  reply_to?: string | null;
  image_url?: string | null;
  document_url?: string | null;
  document_name?: string | null;
  profiles?: { display_name: string | null; avatar_url: string | null } | null;
}

type Channel = { id: string; slug: string; name: string };
type Media = { object_path: string; original_name: string; mime_type: string; byte_size: number };
type CerebroMessage = {
  id: string; channel_id: string; author_user_id: string;
  author: { display_name: string | null; avatar_url: string | null };
  parent_message_id: string | null; content: string; created_at: string;
  edited_at: string | null; media: Media[];
};

const PAGE_SIZE = 30;
const CHAT_MEDIA_BUCKET = 'forjahub-chat-media';

async function rpc<T>(name: string, params: Record<string, unknown> = {}): Promise<T> {
  const { data, error } = await (supabase as any).rpc(name, params);
  if (error) throw error;
  return data as T;
}

async function resolveChannel(tabSlug: string): Promise<Channel | null> {
  const channels = await rpc<Channel[]>('list_chat_channels');
  return channels.find(channel => channel.slug === tabSlug)
    || channels.find(channel => channel.slug === 'geral')
    || channels[0]
    || null;
}

async function mapMessages(items: CerebroMessage[]): Promise<ChatMessage[]> {
  const files = items.flatMap(item => item.media || []);
  const paths = files.map(file => file.object_path);
  const signed = paths.length
    ? await supabase.storage.from(CHAT_MEDIA_BUCKET).createSignedUrls(paths, 15 * 60)
    : { data: [], error: null };
  if (signed.error) throw signed.error;
  const urls = new Map(paths.map((path, index) => [path, signed.data?.[index]?.signedUrl]));
  return items.map(item => {
    const image = item.media?.find(file => file.mime_type.startsWith('image/'));
    const document = item.media?.find(file => !file.mime_type.startsWith('image/'));
    return {
      id: item.id, user_id: item.author_user_id, content: item.content,
      created_at: item.created_at, edited_at: item.edited_at,
      reply_to: item.parent_message_id,
      image_url: image ? urls.get(image.object_path) || null : null,
      document_url: document ? urls.get(document.object_path) || null : null,
      document_name: document?.original_name || null, profiles: item.author,
    };
  });
}

async function fetchMessages(channelId: string, before?: { created_at: string; id: string } | null) {
  const page = await rpc<{ items: CerebroMessage[]; next_cursor: { created_at: string; id: string } | null }>('list_chat_messages', {
    p_channel_id: channelId, p_before_created_at: before?.created_at || null,
    p_before_id: before?.id || null, p_limit: PAGE_SIZE,
  });
  return { messages: await mapMessages(page.items || []), nextCursor: page.next_cursor };
}

async function uploadChatFile(messageId: string, file: File) {
  if (file.size <= 0 || file.size > 15 * 1024 * 1024) throw new Error('O anexo deve ter no máximo 15 MB.');
  const { data } = await supabase.auth.getUser();
  if (!data.user) throw new Error('Sessão expirada.');
  const safe = file.name.normalize('NFKD').replace(/[^a-zA-Z0-9._-]/g, '-').slice(-100) || 'arquivo';
  const path = `chat/${data.user.id}/${messageId}/${crypto.randomUUID()}-${safe}`;
  const { error } = await supabase.storage.from(CHAT_MEDIA_BUCKET).upload(path, file, { contentType: file.type, upsert: false });
  if (error) throw error;
  try {
    await rpc('register_chat_media', {
      p_message_id: messageId, p_object_path: path, p_original_name: file.name,
      p_mime_type: file.type, p_byte_size: file.size,
    });
  } catch (registrationError) {
    await supabase.storage.from(CHAT_MEDIA_BUCKET).remove([path]);
    throw registrationError;
  }
}

export async function fetchInitialMessages(tabSlug: string): Promise<{ messages: ChatMessage[]; hasMore: boolean }> {
  const channel = await resolveChannel(tabSlug);
  if (!channel) return { messages: [], hasMore: false };
  const page = await fetchMessages(channel.id);
  return { messages: page.messages, hasMore: Boolean(page.nextCursor) };
}

export function useChatMessages(scrollRef: React.RefObject<HTMLDivElement>, tabSlug: string) {
  const { user } = useAuth();
  const { data: currentProfile } = useCurrentProfile(user?.id);
  const qc = useQueryClient();
  const [sending, setSending] = useState(false);
  const [olderMessages, setOlderMessages] = useState<ChatMessage[]>([]);
  const [nextCursor, setNextCursor] = useState<{ created_at: string; id: string } | null>(null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const channelQuery = useQuery({ queryKey: ['cerebro-chat-channel', tabSlug], queryFn: () => resolveChannel(tabSlug), staleTime: 10 * 60_000 });
  const channelId = channelQuery.data?.id || null;
  const queryKey = useMemo(() => ['community-chat-messages', channelId], [channelId]);
  const messageQuery = useQuery({
    queryKey,
    queryFn: async () => channelId ? fetchMessages(channelId) : { messages: [], nextCursor: null },
    enabled: Boolean(channelId), staleTime: 60_000, refetchOnWindowFocus: false,
  });
  const currentMessages = messageQuery.data?.messages || [];
  const messages = [...olderMessages, ...currentMessages];

  useEffect(() => {
    setOlderMessages([]);
    setNextCursor(messageQuery.data?.nextCursor || null);
  }, [channelId, messageQuery.data?.nextCursor]);

  useEffect(() => {
    if (!channelId) return;
    void supabase.realtime.setAuth();
    const invalidate = () => void qc.invalidateQueries({ queryKey });
    const channel = supabase.channel(`chat:channel:${channelId}`, { config: { private: true } })
      .on('broadcast', { event: 'INSERT' }, invalidate)
      .on('broadcast', { event: 'UPDATE' }, invalidate)
      .on('broadcast', { event: 'DELETE' }, invalidate)
      .subscribe();
    return () => { void supabase.removeChannel(channel); };
  }, [channelId, qc, queryKey]);

  const refresh = useCallback(async () => {
    await qc.invalidateQueries({ queryKey });
    setTimeout(() => scrollToBottom(scrollRef), 50);
  }, [qc, queryKey, scrollRef]);

  const sendMessage = async (text: string, replyTo?: string | null, imageFile?: File | null, documentFile?: File | null) => {
    if (!channelId || !user || (!text.trim() && !imageFile && !documentFile)) return false;
    setSending(true);
    try {
      const messageId = await rpc<string>('send_chat_message', {
        p_channel_id: channelId, p_content: text.trim() || '📎 Anexo',
        p_parent_message_id: replyTo || null, p_mentioned_user_ids: [],
      });
      for (const file of [imageFile, documentFile].filter(Boolean) as File[]) await uploadChatFile(messageId, file);
      await refresh();
      return true;
    } catch (error) {
      console.error('[Cérebro chat send]', error);
      toast.error('Erro ao enviar mensagem');
      return false;
    } finally { setSending(false); }
  };

  const editMessage = async (messageId: string, text: string) => {
    try { await rpc('edit_chat_message', { p_message_id: messageId, p_content: text.trim() }); await refresh(); }
    catch { toast.error('Erro ao editar mensagem'); }
  };
  const deleteMessage = async (messageId: string) => {
    try { await rpc('delete_chat_message', { p_message_id: messageId }); await refresh(); }
    catch { toast.error('Erro ao deletar mensagem'); }
  };
  const loadOlder = async () => {
    if (!channelId || !nextCursor || loadingOlder) return;
    setLoadingOlder(true);
    try {
      const page = await fetchMessages(channelId, nextCursor);
      setOlderMessages(previous => [...page.messages, ...previous]);
      setNextCursor(page.nextCursor);
    } finally { setLoadingOlder(false); }
  };

  void currentProfile;
  return { messages, loading: channelQuery.isLoading || messageQuery.isLoading, sending,
    sendMessage, editMessage, deleteMessage, hasMore: Boolean(nextCursor), loadOlder, loadingOlder };
}
